import { FormikContextType, getIn, useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import pluralize from 'pluralize'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { useAuth } from 'src/providers/AuthProvider'
import { FormLayoutProvider } from 'src/providers/FormLayoutProvider'
import { useStepper } from 'src/providers/StepperProvider'
import { useUser } from 'src/providers/UserProvider'

import { Currency } from 'src/components/atoms/Currency'
import { Divider } from 'src/components/atoms/Divider'
import { InternalLink } from 'src/components/atoms/Link'
import { Note } from 'src/components/atoms/Note'
import { Heading2, SpanDisplay } from 'src/components/atoms/Typography'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { FormField } from 'src/components/molecules/FormField'
import { StepperButtons } from 'src/components/molecules/StepperButtons'
import { fundingQuantityOptions } from 'src/components/organisms/forms/DonateForm/helper'
import {
  DonateFormValues,
  DonateFrequencyTab
} from 'src/components/organisms/forms/DonateForm/types'

import {
  useGetRegistryByIdQuery,
  useGetAccountSubscriptionDetailsQuery
} from 'src/config/generated/graphql'
import {
  allActivitiesPath,
  carbonCalculatorPath,
  donatePath,
  registryGiftPath,
  registryGiftStepPath,
  viewAccountBillingDetailsPath
} from 'src/config/paths'
import { useCache } from 'src/hooks/useCache'
import { FormFieldTypes } from 'src/types/form'
import { trackEvent } from 'src/utils/ga'

import { MessageType } from '../../validationSchema'
import { DonateImpactTiles } from './DonateImpactTiles'
import { getTileParams, toWordsCapped } from './helpers'

export const Trees = () => {
  const router = useRouter()
  const { query } = router

  const { nextStep } = useStepper()
  const { user } = useUser()
  const { loginWithCustomRedirect, signupWithCustomRedirect } = useAuth()

  const formikContext: FormikContextType<DonateFormValues> = useFormikContext()
  const { isValid, setFieldValue, values, validateField, validateForm } =
    formikContext

  const registryId = useMemo(() => {
    return getIn(values, 'RegistryId')
  }, [values])

  const isRegistryGiftForm = !!registryId

  const { data: registry } = useGetRegistryByIdQuery({
    variables: {
      id: registryId
    },
    skip: !isRegistryGiftForm
  })

  const [activeTab, setActiveTab] = useState<DonateFrequencyTab>(
    (values.DonationType as DonateFrequencyTab) || DonateFrequencyTab.Once
  )

  const { setValue } = useCache({
    key: 'donateForm'
  })

  const { data: subscription } = useGetAccountSubscriptionDetailsQuery({
    variables: {
      funderId: user?.Account?.FunderId!
    },
    skip: values.DonationType !== DonateFrequencyTab.Monthly
  })

  // Price + Amount syncing (and the underlying useProductPrice fetch) moved
  // into DonateForm's PriceSyncEffect so the Payment step has correct values
  // even when the Trees step is skipped (the /get-started → /donate/step/1
  // package flow). This step reads `values.PricePerTree` from Formik for any
  // display / GA event that needs the live price.

  const treeQuantity = useMemo(
    () =>
      parseInt(
        (values.Quantity === 'other'
          ? values.QuantityOther
          : values.Quantity) || '0',
        10
      ),
    [values]
  )

  useEffect(() => {
    // productPrice + PricePerTree fetch lives in DonateForm's PriceSyncEffect
    // now — no need to call getProductPrice here. The GA event still reads
    // the latest price from Formik so it fires correctly once PricePerTree
    // populates.
    const livePrice = parseFloat(values.PricePerTree || '0')
    if (livePrice) {
      trackEvent('view_item_list', {
        items: fundingQuantityOptions.map((option, idx) => ({
          item_id: `trees-${option.value}`,
          item_name: option.label,
          item_list_name: 'Tree',
          item_category: 'Donate',
          quantity: option.value,
          price: livePrice,
          index: idx
        }))
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.PricePerTree])

  const onSubmit = async () => {
    // Manually set cache value so we get the most up-to-date formState
    setValue(values)

    trackEvent('add_to_cart', {
      items: [
        {
          item_id: values.QuantityOther
            ? 'trees-other'
            : `${values.Quantity} ${pluralize(
                'tree',
                Number(values.Quantity)
              )}`,
          item_name: values.QuantityOther
            ? 'Other amount'
            : `${values.Quantity} ${pluralize(
                'tree',
                Number(values.Quantity)
              )}`,
          item_list_name: 'Tree',
          item_category: 'Donate',
          quantity: values.QuantityOther || values.Quantity,
          price: parseFloat(values.PricePerTree || '0')
        }
      ]
    })

    delete query.quantity

    if (user) return nextStep()

    if (values.DonationType === DonateFrequencyTab.Monthly && !user) {
      return signupWithCustomRedirect(
        donatePath({ queryParams: { stepIndex: 1 } })
      )
    }

    if (isRegistryGiftForm) {
      return loginWithCustomRedirect(
        registryGiftPath({
          routeParams: { registryUri: registry?.Registry_by_pk?.Uri! },
          queryParams: { stepIndex: 1 }
        }),
        registryGiftStepPath({
          routeParams: {
            registryUri: registry?.Registry_by_pk?.Uri!,
            stepIndex: '1'
          }
        })
      )
    }

    return router.replace(donatePath({ queryParams: { stepIndex: 1 } }))
  }

  const stripQuantityFromAddressBar = () => {
    if (!query.quantity) return

    const [path, search] = router.asPath.split('?')
    const params = new URLSearchParams(search)
    params.delete('quantity')
    const nextParams = params.toString()

    window.history.replaceState(
      window.history.state,
      '',
      nextParams ? `${path}?${nextParams}` : path
    )
  }

  function handleChangeQuantity({
    fieldId,
    quantity
  }: {
    fieldId: string
    quantity: string
  }): void {
    const newQuantity = parseInt(quantity, 10) > 0 ? parseInt(quantity, 10) : 1

    // setFieldValue triggers PriceSyncEffect (watching values.Quantity /
    // values.QuantityOther) which calls getProductPrice with the new quantity.
    setFieldValue(fieldId, newQuantity)

    stripQuantityFromAddressBar()
  }

  const handleChangeTab = (tab: string) => {
    if (tab === activeTab) return

    if (tab === DonateFrequencyTab.Once) {
      setActiveTab(DonateFrequencyTab.Once)
    } else if (tab === DonateFrequencyTab.Monthly) {
      setActiveTab(DonateFrequencyTab.Monthly)
    }

    router.replace(
      {
        query: {
          ...router.query,
          donationType: tab,
          ...(treeQuantity > 0 && { quantity: String(treeQuantity) })
        }
      },
      undefined,
      { shallow: true }
    )
  }

  const quantityLabel = useMemo(() => {
    if (isRegistryGiftForm) {
      return 'Select the number of native trees to fund'
    }
    if (activeTab === DonateFrequencyTab.Monthly) {
      return 'Select the number of native trees to donate each month'
    }
    return 'Select the number of native trees to donate'
  }, [isRegistryGiftForm, activeTab])

  const SubscriptionNote = useMemo(() => {
    if (
      values.DonationType === DonateFrequencyTab.Monthly &&
      subscription?.Subscription.length
    ) {
      return (
        <Note type='infoNew' className='mt-5 md:mt-10'>
          You already have a monthly donation set up. You can update this within{' '}
          <InternalLink
            href={viewAccountBillingDetailsPath}
            color='forestDeep'
            className='underline hover:no-underline'
          >
            your payments page
          </InternalLink>
          .
        </Note>
      )
    }
    return null
  }, [subscription?.Subscription.length, values.DonationType])

  const tileParams = useMemo(() => {
    return getTileParams(
      treeQuantity,
      values.DonationType === DonateFrequencyTab.Monthly
    )
  }, [treeQuantity, values.DonationType])

  return (
    <>
      {!isRegistryGiftForm ? (
        <FormField
          type={FormFieldTypes.tabSwitch}
          id='DonationType'
          switchOptions={[
            { label: 'MONTHLY', value: DonateFrequencyTab.Monthly },
            { label: 'ONE-OFF', value: DonateFrequencyTab.Once }
          ]}
          label=''
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleChangeTab(e.target.value)
          }}
          className='w-full'
          switchColor='lime'
        />
      ) : null}
      {!isRegistryGiftForm ? SubscriptionNote : null}
      {values.DonationType === DonateFrequencyTab.Monthly &&
      subscription?.Subscription.length ? null : (
        <>
          <FormField
            id='Quantity'
            label={
              <div
                className={`${
                  isRegistryGiftForm ? 'mt-0' : 'mt-5'
                } md:mt-10 mb-4`}
              >
                <TypographyRebrand variant='h5' color='forestDeep'>
                  {quantityLabel}
                </TypographyRebrand>
              </div>
            }
            className='mb-10'
            labelClassName='text-shark!'
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleChangeQuantity({
                fieldId: 'Quantity',
                quantity: e.target.value
              })
            }}
            options={fundingQuantityOptions}
            type={FormFieldTypes.treeQuantityRadioGroupRebrand}
            conditionForConditionalComponent={values.Quantity === 'other'}
            conditionalComponent={
              <FormField
                id='QuantityOther'
                min={0}
                label=''
                helpText='Enter amount (e.g., 20)'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleChangeQuantity({
                    fieldId: 'QuantityOther',
                    quantity: e.target.value
                  })
                }}
                type={FormFieldTypes.number}
              />
            }
            conditionalComponentId='QuantityOther'
          />
          <div className='mb-8 text-right'>
            <div>
              <Heading2
                className={`m-0! font-body-rebrand! text-[32px]! leading-[40px]! md:text-[40px]! md:leading-[50px]! font-bold! ${
                  Number(values.Amount) === 0
                    ? 'text-cloudGrey!'
                    : 'text-forestDeep'
                }`}
              >
                <Currency amount={values.Amount} />
              </Heading2>
              <SpanDisplay className='m-0! ml-2 font-medium text-[16px]'>
                {activeTab === DonateFrequencyTab.Monthly ? 'per month' : ''}
              </SpanDisplay>
            </div>
            <SpanDisplay className='m-0! font-medium text-[14px] text-forestDeep'>
              (<Currency amount={parseFloat(values.PricePerTree || '0')} /> NZD
              per tree)
            </SpanDisplay>
          </div>
          {treeQuantity > 0 && tileParams.length ? (
            <div className='flex flex-col gap-[10px]'>
              <TypographyRebrand variant='h5' color='forestDeep'>
                {values.DonationType === DonateFrequencyTab.Monthly
                  ? `After one year, planting ${toWordsCapped(
                      treeQuantity
                    )} native ${pluralize(
                      'tree',
                      treeQuantity
                    )} each month could…`
                  : `Planting ${toWordsCapped(treeQuantity)} native ${pluralize(
                      'tree',
                      treeQuantity
                    )} could...`}
              </TypographyRebrand>

              <DonateImpactTiles items={tileParams} />
              <span className='text-forestDeep font-medium text-[12px] leading-[18px]'>
                Expected tCO₂e removal is measured over 50 years of continuous
                plant growth.{' '}
                <InternalLink
                  color='none'
                  className='font-medium text-forestDeep! underline'
                  href={carbonCalculatorPath}
                >
                  Learn more.
                </InternalLink>
              </span>
            </div>
          ) : null}
          <Divider className='my-8! sm:my-10!' />
          <div className='flex flex-col gap-[10px] mb-10!'>
            <FormLayoutProvider margin='base'>
              <TypographyRebrand variant='h5' color='forestDeep'>
                Preferences (optional)
              </TypographyRebrand>
              <div>
                {isRegistryGiftForm ? null : (
                  <FormField
                    id='SelectPlantingRegion'
                    labelClassName='sr-only'
                    className='m-0!'
                    label=''
                    formFieldClassName='font-medium [&_svg]:fill-lavenderBright! [&_span]:text-forestDeep!'
                    checkboxLabel='Select a region for your trees to be planted'
                    type={FormFieldTypes.checkbox}
                    conditionForConditionalComponent={
                      !!values.SelectPlantingRegion
                    }
                    conditionalComponent={
                      <FormField
                        className='mb-5!'
                        id='PlantingRegionId'
                        labelClassName='sr-only'
                        label=''
                        description={
                          <div className='mt-[10px]'>
                            <TypographyRebrand
                              variant='paragraph'
                              color='forestDeep'
                            >
                              We&apos;ll try to allocate your tree/s to your
                              preferred region, but they may be planted where
                              needed most.{' '}
                              {activeTab === DonateFrequencyTab.Monthly
                                ? 'You can change this later.'
                                : ''}
                            </TypographyRebrand>
                          </div>
                        }
                        type={FormFieldTypes.regionSelect}
                        regionsOnly
                      />
                    }
                  />
                )}
                <FormField
                  labelClassName='sr-only'
                  type={FormFieldTypes.checkbox}
                  className='m-0!'
                  checkboxLabel='Post a personal message'
                  label=''
                  formFieldClassName='font-medium [&_svg]:fill-lavenderBright! [&_span]:text-forestDeep!'
                  id='PostMessage'
                  onChange={async e => {
                    if (e.target.checked) {
                      await setFieldValue(
                        'UserEventMessageType',
                        MessageType.Anonymous
                      )
                    } else {
                      await setFieldValue(
                        'UserEventMessageType',
                        MessageType.None
                      )
                      await setFieldValue('PublicName', '')
                      await setFieldValue('PublicMessage', '')
                    }
                    await validateForm()
                  }}
                  conditionForConditionalComponent={!!values.PostMessage}
                  conditionalComponent={
                    <div>
                      <div className='mb-4 mt-[10px]'>
                        <TypographyRebrand
                          variant='paragraph'
                          color='forestDeep'
                        >
                          {isRegistryGiftForm ? (
                            <>
                              Let them know why you&apos;re supporting their
                              cause.
                            </>
                          ) : (
                            <>
                              Your message will be displayed on our website on
                              the{' '}
                              <InternalLink
                                openInNewTab
                                href={allActivitiesPath}
                              >
                                activity page.
                              </InternalLink>{' '}
                              {activeTab === DonateFrequencyTab.Monthly
                                ? 'You can change this each month.'
                                : ''}
                            </>
                          )}
                        </TypographyRebrand>
                      </div>
                      <FormField
                        id='UserEventMessageType'
                        label=''
                        className='mb-4! [&_[role=radiogroup]_span]:text-forestDeep!'
                        type={FormFieldTypes.radioGroup}
                        onChange={async v => {
                          if (v.target.value === MessageType.Public) {
                            await setFieldValue(
                              'PublicName',
                              user?.Account?.Funder?.Name || ''
                            )
                          } else if (v.target.value === MessageType.Anonymous) {
                            await setFieldValue('PublicName', '')
                          } else {
                            await setFieldValue('PublicName', '')
                            await setFieldValue('PublicMessage', '')
                          }
                          await validateField('PublicName')
                        }}
                        conditionForConditionalComponent={
                          values.UserEventMessageType !== MessageType.None
                        }
                        conditionalComponent={
                          <>
                            {values.UserEventMessageType ===
                            MessageType.Anonymous ? null : (
                              <FormField
                                id='PublicName'
                                label='Name to display'
                                labelClassName='font-semibold! text-base! leading-[22px]! text-charcoal!'
                                type={FormFieldTypes.text}
                              />
                            )}
                            <FormField
                              id='PublicMessage'
                              label='Message'
                              labelClassName='font-semibold! text-base! leading-[22px]! text-charcoal!'
                              type={FormFieldTypes.textarea}
                            />
                          </>
                        }
                        options={[
                          {
                            label: 'Message with a name',
                            value: MessageType.Public
                          },
                          {
                            label: 'Anonymous message',
                            value: MessageType.Anonymous
                          }
                        ]}
                      />
                    </div>
                  }
                />
              </div>
            </FormLayoutProvider>
          </div>
          <StepperButtons
            isSubmitDisabled={!isValid}
            submitText='Next'
            onSubmit={onSubmit}
          />
        </>
      )}
    </>
  )
}
